import React, { useState } from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  CheckCircle,
  AlertTriangle,
  AlertCircle,
  Phone,
  Pill,
  Activity,
  Download,
} from "lucide-react";
import type { OutcomeNode as OutcomeNodeType, SupplyDetails, Pathway } from "@/lib/pharmacyFirst/types";
import { SIDE_EFFECTS_BY_DRUG } from "@/lib/pharmacyFirst/drugs/side-effects";
import { getDrug } from "@/lib/pharmacyFirst/drugs";

interface OutcomeNodeProps {
  node: OutcomeNodeType;
  isDarkMode?: boolean;
  supplyDetails?: SupplyDetails;
  drugId?: string;
  /** Optional pharmacist counselling points (typically from selected PGD) */
  counsellingPoints?: string[];
  /** Optional cautions list (typically from selected PGD) */
  cautions?: { id: string; text: string; adviceText?: string }[];
  /** Optional pathway for resolving leaflet references */
  pathway?: Pathway;
  onClose: () => void;
  onPrint?: () => void;
  /**
   * Called when the user wants to open a linked calculator from this outcome.
   * The host (PathwayEngine → Index) opens the calculator in the actions
   * column using the existing manualCalculators flow.
   */
  onOpenCalculator?: (calculatorId: string) => void;
  onCreateTreatmentPlan?: () => void | Promise<void>;
  treatmentPlanExists?: boolean;
}

export function OutcomeNode({
  node,
  isDarkMode = false,
  supplyDetails,
  drugId,
  counsellingPoints,
  cautions,
  pathway,
  onClose,
  onPrint,
  onOpenCalculator,
  onCreateTreatmentPlan,
  treatmentPlanExists = false,
}: OutcomeNodeProps) {
  const [treatmentPlanCreated, setTreatmentPlanCreated] = useState(false);
  const linkedCalculatorButton =
    node.linkedCalculatorId && onOpenCalculator ? (
      <Button
        variant="outline"
        size="lg"
        className="w-full"
        onClick={() => onOpenCalculator(node.linkedCalculatorId!)}
      >
        <Activity className="h-4 w-4 mr-2" />
        {node.linkedCalculatorLabel || "Open calculator"}
      </Button>
    ) : null;

  const patientLeafletsSection =
    node.leafletRefs && node.leafletRefs.length > 0 && pathway?.leaflets ? (
      <div
        className={`border rounded-lg p-4 space-y-2 ${
          isDarkMode ? "border-purple-700 bg-purple-950" : "border-purple-200 bg-purple-50"
        }`}
      >
        <h4
          className={`font-semibold text-sm ${
            isDarkMode ? "text-purple-200" : "text-purple-900"
          }`}
        >
          Patient information leaflets
        </h4>
        <div className="space-y-2">
          {node.leafletRefs.map((ref) => {
            const leaflet = pathway.leaflets[ref];
            if (!leaflet) return null;
            const isWelsh = leaflet.language === "cy";
            return (
              <a
                key={ref}
                href={leaflet.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center gap-2 p-2 rounded text-sm font-medium transition ${
                  isDarkMode
                    ? "hover:bg-purple-900 text-purple-300"
                    : "hover:bg-purple-100 text-purple-700"
                }`}
              >
                <Download className="h-4 w-4" />
                <span>{leaflet.title}</span>
                {isWelsh && (
                  <span
                    className={`ml-auto px-2 py-0.5 rounded-full text-xs font-semibold ${
                      isDarkMode
                        ? "bg-blue-900 text-blue-200 border border-blue-700"
                        : "bg-blue-100 text-blue-800 border border-blue-300"
                    }`}
                  >
                    Cymraeg
                  </span>
                )}
              </a>
            );
          })}
        </div>
      </div>
    ) : null;

  const renderSupply = () => (
    <div className="space-y-4">
      <Alert className="bg-green-50 border-green-200">
        <CheckCircle className="h-5 w-5 text-green-600" />
        <AlertTitle className="text-lg font-bold mt-2 text-green-900">
          {node.title}
        </AlertTitle>
      </Alert>

      {supplyDetails && (() => {
        let displayDrugName = supplyDetails.drugId;
        try {
          displayDrugName = getDrug(supplyDetails.drugId).name;
        } catch {
          // fall back to raw id
        }
        const isTopical = supplyDetails.resolvedDoseMg === 0 || displayDrugName.toLowerCase().includes("cream") || displayDrugName.toLowerCase().includes("ointment");
        return (
          <div
            className={`border rounded-lg p-4 space-y-3 ${
              isDarkMode
                ? "border-gray-600 bg-gray-700"
                : "border-green-200 bg-green-50"
            }`}
          >
            <div>
              <h4 className="font-semibold">Prescription Details</h4>
              <p className="text-sm mt-1">
                <strong>Drug:</strong> {displayDrugName}
              </p>
              {!isTopical && (
                <p className="text-sm">
                  <strong>Dose:</strong> {typeof supplyDetails.resolvedDoseMg === "number" ? `${supplyDetails.resolvedDoseMg}mg` : supplyDetails.resolvedDoseMg}
                </p>
              )}
              <p className="text-sm">
                <strong>Instruction:</strong> {supplyDetails.instructionText}
              </p>
              <p className="text-sm">
                <strong>Quantity:</strong> {supplyDetails.quantity.label}
              </p>
              <p className="text-sm">
                <strong>Duration:</strong> {supplyDetails.totalDurationDays} days
              </p>
            </div>
          </div>
        );
      })()}

      {node.safetyNetting && node.safetyNetting.length > 0 && (
        <div
          className={`border rounded-lg p-4 ${
            isDarkMode ? "border-gray-600 bg-gray-700" : "border-blue-200 bg-blue-50"
          }`}
        >
          <h4 className="font-semibold mb-2">Safety Netting</h4>
          <ul className="text-sm space-y-1">
            {node.safetyNetting.map((point, idx) => (
              <li key={idx} className="flex gap-2">
                <span>•</span>
                <span>{point}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {drugId && SIDE_EFFECTS_BY_DRUG[drugId] && (
        <details
          className={`border rounded-lg p-3 ${
            isDarkMode ? "border-orange-700 bg-orange-950/30" : "border-orange-200 bg-orange-50"
          }`}
        >
          <summary className={`cursor-pointer text-sm font-semibold ${
            isDarkMode ? "text-orange-200" : "text-orange-900"
          }`}>
            Possible side effects
          </summary>
          <div className="text-sm space-y-3 mt-3">
            {["common", "uncommon", "rare"].map((freq) => {
              const effects = SIDE_EFFECTS_BY_DRUG[drugId]?.filter(se => se.frequency === freq) || [];
              if (effects.length === 0) return null;
              return (
                <div key={freq}>
                  <p className={`font-semibold text-xs uppercase tracking-wide ${
                    isDarkMode ? "text-orange-300" : "text-orange-800"
                  }`}>
                    {freq === "common" ? "Very Common or Common" : freq === "uncommon" ? "Uncommon" : "Rare"}
                  </p>
                  <ul className="mt-1 space-y-1">
                    {effects.map((effect, idx) => (
                      <li key={idx} className="flex gap-2">
                        <span>•</span>
                        <span>{effect.effect}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </details>
      )}

      {counsellingPoints && counsellingPoints.length > 0 && (
        <details
          className={`border rounded-lg p-3 ${
            isDarkMode ? "border-gray-600 bg-gray-700" : "border-gray-200 bg-white"
          }`}
        >
          <summary className="cursor-pointer text-sm font-semibold">
            Pharmacist counselling points ({counsellingPoints.length})
          </summary>
          <ul className="text-sm space-y-1 mt-2">
            {counsellingPoints.map((point, idx) => (
              <li key={idx} className="flex gap-2">
                <span>•</span>
                <span>{point}</span>
              </li>
            ))}
          </ul>
        </details>
      )}

      {cautions && cautions.length > 0 && (
        <details
          className={`border rounded-lg p-3 ${
            isDarkMode ? "border-amber-300 bg-amber-50" : "border-amber-700 bg-amber-950/30"
          }`}
        >
          <summary className="cursor-pointer text-sm font-semibold">
            Cautions ({cautions.length})
          </summary>
          <ul className="text-sm space-y-1 mt-2">
            {cautions.map((c) => (
              <li key={c.id} className="flex gap-2">
                <span>•</span>
                <span>
                  {c.text}
                  {c.adviceText ? ` — ${c.adviceText}` : ""}
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}

      {patientLeafletsSection}

      {onCreateTreatmentPlan && (
        treatmentPlanCreated || treatmentPlanExists ? (
          <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm font-medium text-green-800">
            <CheckCircle className="h-4 w-4 shrink-0" />
            Treatment plan created
          </div>
        ) : (
          <Button
            variant="outline"
            className="w-full"
            onClick={async () => {
              await onCreateTreatmentPlan();
              setTreatmentPlanCreated(true);
            }}
          >
            Create Treatment Plan
          </Button>
        )
      )}

      <div className="flex gap-2">
        {onPrint && (
          <Button onClick={onPrint} variant="outline">
            Print
          </Button>
        )}
        <Button onClick={onClose} className="flex-1">
          Done
        </Button>
      </div>
    </div>
  );

  const renderReferUrgent = () => (
    <div className="space-y-4">
      <Alert variant="destructive">
        <AlertTriangle className="h-5 w-5" />
        <AlertTitle className="text-lg font-bold mt-2">
          {node.title}
        </AlertTitle>
        {node.reason && (
          <AlertDescription className="mt-2">{node.reason}</AlertDescription>
        )}
      </Alert>

      {node.safetyNetting && node.safetyNetting.length > 0 && (
        <div
          className={`border rounded-lg p-4 ${
            isDarkMode ? "border-gray-600 bg-gray-700" : "border-red-200 bg-red-50"
          }`}
        >
          <h4 className="font-semibold mb-2">Action Required</h4>
          <ul className="text-sm space-y-1">
            {node.safetyNetting.map((point, idx) => (
              <li key={idx} className="flex gap-2">
                <span>•</span>
                <span>{point}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {patientLeafletsSection}

      <Button onClick={onClose} variant="destructive" className="w-full">
        Close
      </Button>
    </div>
  );

  const renderReferRoutine = () => (
    <div className="space-y-4">
      <Alert className="bg-yellow-50 border-yellow-200">
        <AlertCircle className="h-5 w-5 text-yellow-600" />
        <AlertTitle className="text-lg font-bold text-yellow-900 leading-tight">
          {node.title}
        </AlertTitle>
        {node.reason && (
          <AlertDescription className="mt-1 text-yellow-800">
            {node.reason}
          </AlertDescription>
        )}
      </Alert>

      {node.safetyNetting && node.safetyNetting.length > 0 && (
        <div
          className={`border rounded-lg p-4 ${
            isDarkMode ? "border-gray-600 bg-gray-700" : "border-yellow-200 bg-yellow-50"
          }`}
        >
          <h4 className="font-semibold mb-2">Advice</h4>
          <ul className="text-sm space-y-1">
            {node.safetyNetting.map((point, idx) => (
              <li key={idx} className="flex gap-2">
                <span>•</span>
                <span>{point}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {(() => {
        const referralOptions = (node as any).referralOptions ?? [
          "GP Practice",
          "Other provider as appropriate",
        ];
        if (!referralOptions.length) return null;
        return (
          <div
            className={`border rounded-lg p-4 ${
              isDarkMode ? "border-gray-600 bg-gray-700" : "border-yellow-200 bg-yellow-50"
            }`}
          >
            <h4 className="font-semibold mb-2">Suggested referral options</h4>
            <ul className="text-sm space-y-1">
              {referralOptions.map((opt: string, idx: number) => (
                <li key={idx} className="flex gap-2"><span>•</span><span>{opt}</span></li>
              ))}
            </ul>
          </div>
        );
      })()}

      {patientLeafletsSection}

      <Button onClick={onClose} className="w-full">
        Close
      </Button>
    </div>
  );

  const renderSelfCare = () => (
    <div className="space-y-4">
      <Alert className="bg-blue-50 border-blue-200">
        <AlertCircle className="h-5 w-5 text-blue-600" />
        <AlertTitle className="text-lg font-bold mt-2 text-blue-900">
          {node.title}
        </AlertTitle>
        {node.reason && (
          <AlertDescription className="mt-2 text-blue-800">
            {node.reason}
          </AlertDescription>
        )}
      </Alert>

      {node.safetyNetting && node.safetyNetting.length > 0 && (
        <div
          className={`border rounded-lg p-4 ${
            isDarkMode ? "border-gray-600 bg-gray-700" : "border-blue-200 bg-blue-50"
          }`}
        >
          <h4 className="font-semibold mb-2">Self-Care Advice</h4>
          <ul className="text-sm space-y-1">
            {node.safetyNetting.map((point, idx) => (
              <li key={idx} className="flex gap-2">
                <span>•</span>
                <span>{point}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {patientLeafletsSection}

      <div className="flex gap-2">
        {onPrint && (
          <Button onClick={onPrint} variant="outline">
            Print Advice
          </Button>
        )}
        <Button onClick={onClose} className="flex-1">
          Done
        </Button>
      </div>
    </div>
  );

  const renderEmergency = () => (
    <div className="space-y-4">
      <Alert variant="destructive">
        <AlertTriangle className="h-5 w-5" />
        <AlertTitle className="text-xl font-bold mt-2">
          {node.title}
        </AlertTitle>
        {node.reason && (
          <AlertDescription className="mt-2">{node.reason}</AlertDescription>
        )}
      </Alert>

      {node.safetyNetting && node.safetyNetting.length > 0 && (
        <div
          className={`border rounded-lg p-4 ${
            isDarkMode ? "border-gray-600 bg-gray-700" : "border-red-200 bg-red-50"
          }`}
        >
          <ul className="text-sm space-y-2 font-semibold">
            {node.safetyNetting.map((point, idx) => (
              <li key={idx} className="flex gap-2">
                <Phone className="h-4 w-4 flex-shrink-0" />
                <span>{point}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {linkedCalculatorButton}

      {patientLeafletsSection}

      <Button onClick={onClose} variant="destructive" size="lg" className="w-full">
        Close
      </Button>
    </div>
  );

  const renderCannotSupply = () => (
    <div className="space-y-4">
      <Alert variant="destructive">
        <AlertTriangle className="h-5 w-5" />
        <AlertTitle className="text-lg font-bold mt-2">
          {node.title}
        </AlertTitle>
        {node.reason && (
          <AlertDescription className="mt-2">{node.reason}</AlertDescription>
        )}
      </Alert>

      {node.safetyNetting && node.safetyNetting.length > 0 && (
        <div
          className={`border rounded-lg p-4 ${
            isDarkMode ? "border-gray-600 bg-gray-700" : "border-red-200 bg-red-50"
          }`}
        >
          <h4 className="font-semibold mb-2">Next Steps</h4>
          <ul className="text-sm space-y-1">
            {node.safetyNetting.map((point, idx) => (
              <li key={idx} className="flex gap-2">
                <span>•</span>
                <span>{point}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {patientLeafletsSection}

      <Button onClick={onClose} className="w-full">
        Close
      </Button>
    </div>
  );

  switch (node.outcomeType) {
    case "supply":
      return renderSupply();
    case "refer_urgent":
      return renderReferUrgent();
    case "refer_routine":
      return renderReferRoutine();
    case "self_care":
      return renderSelfCare();
    case "emergency":
      return renderEmergency();
    case "cannot_supply":
      return renderCannotSupply();
    default:
      return null;
  }
}
